// src/features/documents/hooks/useDocuments.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getDocuments,
  getDocumentStats,
  searchDocuments,
  searchDocumentsSemantic,
  getDocumentDetail,
  getDocumentPreviewUrl,
  getDocumentDownloadUrl,
  getDocumentLinks,
  getTags,
  reconvertDocument,
  recordDocumentView,
  toggleDocumentReaction,
  reembedDocument,
  reclassifyDocument,
  reprocessDocument,
  getDocumentReferences,
} from '../api';

export const documentKeys = {
  all: ['documents'] as const,
  list: (cursor: string | null) => [...documentKeys.all, 'list', cursor] as const,
  search: (query: string) => [...documentKeys.all, 'search', query] as const,
  semantic: (query: string) => [...documentKeys.all, 'semantic', query] as const,
  detail: (id: string) => [...documentKeys.all, 'detail', id] as const,
  previewUrl: (id: string) => [...documentKeys.all, 'preview-url', id] as const,
  downloadUrl: (id: string) => [...documentKeys.all, 'download-url', id] as const,
  links: (id: string) => [...documentKeys.all, 'links', id] as const,
  references: (id: string) => [...documentKeys.all, 'references', id] as const,
  tags: () => [...documentKeys.all, 'tags'] as const,
  stats: () => [...documentKeys.all, 'stats'] as const,
};

export function isDocumentAccessError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error
    && (error.status === 401 || error.status === 403 || error.status === 404);
}

function retryDocumentRead(failureCount: number, error: unknown): boolean {
  return !isDocumentAccessError(error) && failureCount < 1;
}

export function useDocumentStats() {
  return useQuery({
    queryKey: documentKeys.stats(),
    queryFn: getDocumentStats,
    staleTime: 1000 * 60 * 5,
  });
}

export function useDocumentsList(cursor: string | null, searchQuery: string) {
  return useQuery({
    queryKey: searchQuery
      ? documentKeys.search(searchQuery)
      : documentKeys.list(cursor),
    queryFn: () => searchQuery
      ? searchDocuments(searchQuery)
      : getDocuments(cursor),
    refetchInterval: (query) => {
      const docs = query.state.data?.data;
      if (!Array.isArray(docs)) return false;
      const hasProcessing = docs.some((doc) => {
        const s = doc?.currentVersion?.conversionStatus;
        return s === 'pending' || s === 'processing';
      });
      return hasProcessing ? 5_000 : false;
    },
  });
}

export function useSemanticSearch(query: string) {
  return useQuery({
    queryKey: documentKeys.semantic(query),
    queryFn: () => searchDocumentsSemantic(query),
    enabled: !!query,
  });
}

export function useDocumentDetail(id: string | null) {
  const query = useQuery({
    queryKey: documentKeys.detail(id!),
    queryFn: () => getDocumentDetail(id!),
    enabled: !!id,
    select: (res) => res.data,
    retry: retryDocumentRead,
  });
  // TanStack Query retains successful data when a background refetch fails.
  // An access denial must never expose that previously cached private content.
  return { ...query, data: isDocumentAccessError(query.error) ? undefined : query.data };
}

export function useDocumentReferences(id: string | null) {
  return useQuery({
    queryKey: documentKeys.references(id!),
    queryFn: () => getDocumentReferences(id!),
    enabled: !!id,
  });
}

export function useDocumentPreviewUrl(id: string | null, enabled: boolean) {
  const query = useQuery({
    queryKey: documentKeys.previewUrl(id!),
    queryFn: () => getDocumentPreviewUrl(id!),
    enabled: !!id && enabled,
    retry: retryDocumentRead,
  });
  return { ...query, data: enabled && !isDocumentAccessError(query.error) ? query.data : undefined };
}

export function useDocumentDownloadUrl(id: string | null, enabled: boolean) {
  const query = useQuery({
    queryKey: documentKeys.downloadUrl(id!),
    queryFn: () => getDocumentDownloadUrl(id!),
    enabled: !!id && enabled,
    retry: retryDocumentRead,
  });
  return { ...query, data: enabled && !isDocumentAccessError(query.error) ? query.data : undefined };
}

export function useDocumentLinks(id: string | null) {
  return useQuery({
    queryKey: documentKeys.links(id!),
    queryFn: () => getDocumentLinks(id!),
    enabled: !!id,
  });
}

export function useReconvert(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reconvertDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.detail(id) });
      qc.invalidateQueries({ queryKey: documentKeys.previewUrl(id) });
      qc.invalidateQueries({ queryKey: documentKeys.all });
      toast.success('Document reconverted');
    },
    onError: (e) => toast.error('Reconversion failed', { description: e.message }),
  });
}

export function useReembed(id: string) {
  return useMutation({
    mutationFn: () => reembedDocument(id),
    onSuccess: () => {
      toast.success('Embedding regenerated');
    },
    onError: (e) => toast.error('Re-embed failed', { description: e.message }),
  });
}

export function useReclassify(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reclassifyDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.detail(id) });
      qc.invalidateQueries({ queryKey: documentKeys.all });
      toast.success('Document reclassified');
    },
    onError: (e) => toast.error('Reclassify failed', { description: e.message }),
  });
}

export function useReprocess(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => reprocessDocument(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.detail(id) });
      qc.invalidateQueries({ queryKey: documentKeys.previewUrl(id) });
      qc.invalidateQueries({ queryKey: documentKeys.all });
      toast.success('Full reprocessing complete');
    },
    onError: (e) => toast.error('Reprocessing failed', { description: e.message }),
  });
}

export function useTags() {
  return useQuery({
    queryKey: documentKeys.tags(),
    queryFn: getTags,
    staleTime: 10 * 60 * 1000, // tags rarely change
    select: (res) => {
      const map = new Map<string, (typeof res.data)[number]>();
      for (const t of res.data) map.set(t.id, t);
      return map;
    },
  });
}

export function useRecordView(id: string) {
  return useMutation({
    mutationFn: () => recordDocumentView(id),
  });
}

export function useToggleReaction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reaction: 'up' | 'down') => toggleDocumentReaction(id, reaction),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: documentKeys.detail(id) });
      qc.invalidateQueries({ queryKey: documentKeys.all });
    },
    onError: (e) => toast.error('Reaction failed', { description: e.message }),
  });
}
